import { ReactNode, useMemo, useContext, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
  useParams,
  useLocation,
  matchPath
} from "react-router-dom";
import { ApolloProvider } from "@apollo/client";

import {
  LicenseManager,
  AllEnterpriseModule,
  ModuleRegistry,
  provideGlobalGridOptions,
  setupAgTestIds,
  enableDevValidations
} from "ag-grid-enterprise";
import CustomExcelExportView from "features/custom-excel-export/custom-excel-export-view";
import {
  Login,
  HomePage,
  ProjectsListpage,
  // ProjectCreatePage,
  SubmittalDetailspage,
  Users,
  NotFoundPage,
  ProjectDashboardPage,
  RiskReportPage,
  DesignPage,
  SubscriptionReports,
  SubscriptionReportsExport,
  GCProjectDashboardExport,
  MaterialFeatureLogPage
} from "pages";
import PrivateRoute from "ProtectedRoute";
import ForgetPassword from "pages/forgetpassword";
import SetupAccount from "pages/setup_account";
import SetPassword from "pages/setpassword";
import ChangePassword from "pages/changepassword";
import CompanyList from "pages/companies";
import ProjectProvider, {
  isPermissionNotGrantted,
  ProjectContext,
  TProjectContext
} from "context/ProjectProvider";
import FeatureLogNavProvider from "context/FeatureLogNavProvider";
import { TourProvider } from "components/tour";
import SubscriptionProvider, {
  SubscriptionContext,
  RoleSubscriptionEnum
} from "context/SubscriptionProvider";
import AcceptInvitePage from "pages/accept-invite/acceptInvite";
import CreateProjectPage from "pages/project-create/create-project";
import { Navbar } from "components";
import ArchivedProjectBanner from "components/archived-project-banner";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./ag-grid-override.scss";
import "./app.scss";

import { PageTitleProvider } from "context/PageTitleProvider";
import { useGetGQLClientForSubscription } from "hooks/init-gql-client";
import { installTokenRefreshOnWake } from "services/token-refresh";
import { installInactivityLogout } from "services/session-manager";
import { installConnectivityNotices } from "services/connectivity";
import {
  installTabTracking,
  isTabTrackingEnabledForHost
} from "services/tab-tracker";
import { alwaysOpenFirstColumnMenuTab } from "components/ciq-ag-grid/column-menu-popup";
import { getHeaderSortMenuItems } from "components/ciq-ag-grid/header-sort-menu";
import { AG_GRID_LICENSE } from "config/config";
import SubscriptionSettingsPage from "pages/subscription-settings";
import { setGAUserId, updateGAUserProperties } from "utils/analytics";
import ProjectSettingManu from "pages/project-setting-menu";
import AdminApp from "admin-app/admin-app";
import { setAccessTokenAndSubscriptionId } from "services/auth";
import RiskReportExportPDF from "pages/reports-menu/project-reports/risk-report/risk-report-export-pdf";
import ScheduleImpactExportPDF from "pages/schedule/schedule-page/compare-schedule-3/schedule-impact-export-pdf";
import OptimizedPrintView from "pages/schedule/schedule-page/compare-schedule-3/optimized-print-view";
import ReportsPageManu from "pages/reports-menu";
import SubscriptionReportsMenu from "pages/reports-menu/subscription-reports-menu";
import UpcomingSubmittals from "pages/reports-menu/project-reports/upcoming-workflows";

import PreconstructionDetailsPage from "pages/preconstruction-details";
import ScheduleActivitiesReport from "pages/reports-menu/project-reports/schedule-activities-report";
import ActivityLogReport from "pages/reports-menu/audit-reports/activity-log/activity-log";
import PreconstructionLogPage from "pages/preconstruction-log-page";
import DesignPackageLogPage from "pages/design-package-log-page";
import DesignPackageDetailPage from "pages/design-package-details-page";
import DesignScopePage from "pages/design-scope-page";
import ProjectScheduleCompare from "pages/schedule/schedule-page/compare-schedule-2";
import ProjectSchedules from "pages/schedule/schedule-page/project-schedule";
import SchedulesInOpc from "pages/schedule/schedule-page/schedules-in-opc";
import useHasOpcImportFailure from "pages/schedule/schedule-page/schedules-in-opc/use-has-opc-import-failure";
import CIQTabs from "components/ciq-tabs/ciq-tabs";
import CIQSubTabs from "components/ciq-tabs/ciq-sub-tabs";
import ScheduleMenuTabs from "pages/schedule-manu-tabs/schedule-manu-tabs";
import MaterialPlanning from "pages/material-planning";
import InAppUpcomingMilestonesReport from "pages/reports-menu/project-reports/upcoming-milestones/in-app-report/in-app-upcoming-milestones-report";
import ChangePasswordOnExpiry from "pages/changepassword/change-password-on-expiry";

import {
  MaterialCiqIcon,
  ScheduleCiqIcon,
  SubmittalCiqIcon
} from "components/svg-icons";
import ProjectDashboardNavBar from "components/navbar/project-dashboard-nav-bar";
import SubmittalLogLandingPage from "pages/submittal-log/submittal-log-landing-page";
import FeatureMaterialDetailsLandingPage from "pages/material-details/material-details-landing-page";
import FeatureScheduleDetailsLandingPage from "pages/schedule/schedule-page/project-schedule/project-schedule-details/schedule-details-landing-page";
import FeatureMaterialLinking from "pages/feature-entity-linking-page/feature-material";
import FeatureSubmittalLinking from "pages/feature-entity-linking-page/feature-submittal";
import useCheckVersionUpdates from "hooks/check-version-updates";
import TokenValidationGate from "components/TokenValidationGate";
import MaterialExtractedLogPage from "pages/material-extracted-log";
import InternalScRequestPage from "pages/internal-sc-request";
import CustomReportPage from "pages/reports-menu/project-reports/feature-custom-reports/custom-report-page";
import SSOLogin from "pages/sso-login/sso-login";
import SSOCallback from "pages/sso-login/sso-callback";
import SubscriptionSelect from "pages/subscription-select/subscription-select";
import AccountLocked from "pages/login/account-locked";
import PrintViewLayout from "pages/print-view-layout";
import { Spin, Tag } from "antd";
import useValidateRouteAccess from "hooks/validate-route-access";
import ExtractionCompletionAlert from "pages/material-extracted-log/components/extraction-completion-alert";
import useValidateSubscriptionRouteAccess from "hooks/validate-subscription-route-access";
import { ErrorBoundary } from "@sentry/react";
import SubmittalLogUntracked from "pages/submittal-log-untracked";
import BidPackageCiqIcon from "components/svg-icons/bid-package-ciq-icon";
import DesignPackageCiqIcon from "components/svg-icons/design-package-ciq-icon";
import DesignScopeCiqIcon from "components/svg-icons/design-scope-ciq-icon";
import PreconCiqIcon from "components/svg-icons/precon-ciq-icon";
import ConstructionCiqIcon from "components/svg-icons/construction-ciq-icon";
import { BLANKS_FILTER_KEY, ProjectPermissionEnum } from "constants/index";
import {
  getProjectHomePath,
  isPreconstructionProjectHome
} from "utils/project-home-path";
import { usePreconModuleBodyClass } from "hooks/use-precon-module-body-class";

LicenseManager.setLicenseKey(AG_GRID_LICENSE);
// Register all enterprise features
ModuleRegistry.registerModules([AllEnterpriseModule]);

if (import.meta.env.DEV) {
  enableDevValidations();
}

setupAgTestIds();

// Mark all grids as using legacy themes. Deep-merge so `defaultColDef.menuTabs`
// is inherited even when a grid supplies its own `defaultColDef`. Header menus
// are sort-only unless a grid supplies its own `getMainMenuItems`.
// `postProcessPopup` always re-selects the first tab so AG Grid does not
// restore the last opened tab (`generalMenuTab`) on the next open.
provideGlobalGridOptions(
  {
    theme: "legacy",
    columnMenu: "legacy",
    getMainMenuItems: getHeaderSortMenuItems,
    postProcessPopup: alwaysOpenFirstColumnMenuTab,
    defaultColDef: {
      menuTabs: ["filterMenuTab", "generalMenuTab"]
    },
    headerHeight: 36,
    tooltipShowMode: "whenTruncated",
    enableFilterHandlers: true,
    tooltipShowDelay: 0,
    tooltipHideDelay: 2000,
    tooltipTrigger: "hover",
    // One wording for "no value" across the product (CIQ-12252). AG Grid's simple
    // number/date filters label these options "Blank"/"Not blank" while the set
    // filter says "(Blanks)"; align them. The `blanks` key already defaults to
    // "(Blanks)", so it is deliberately not overridden here.
    localeText: {
      blank: BLANKS_FILTER_KEY,
      notBlank: `Not ${BLANKS_FILTER_KEY}`,
      filterSummaryBlank: `is ${BLANKS_FILTER_KEY}`,
      filterSummaryNotBlank: `is not ${BLANKS_FILTER_KEY}`
    }
  },
  "deep"
);

// TODO: make routing config for better route management
type LayoutProps = {
  children: ReactNode;
};

function NoMenuLayout(props: LayoutProps) {
  const { children } = props;
  return (
    <div className="flex flex-col">
      <Navbar />
      {children}
    </div>
  );
}

function HomeLayout(props: LayoutProps) {
  const { children } = props;
  const {
    isFeatureFlagEnabled: isFeatureFlagSubscriptionEnabled,
    subscriptionRole
  } = useContext(SubscriptionContext);
  const isUsageDashboardEnabled = isFeatureFlagSubscriptionEnabled(
    "USAGE_SUMMARY_DASHBOARD"
  );
  const isExecutiveOverviewDashboardEnabled = isFeatureFlagSubscriptionEnabled(
    "EXECUTIVE_OVERVIEW_DASHBOARD"
  );
  const isSubscriptionActivityLogEnabled = isFeatureFlagSubscriptionEnabled(
    "SUBSCRIPTION_ACTIVITY_LOG"
  );

  const isValidRoute = useValidateSubscriptionRouteAccess({
    subscriptionRole
  });

  const menuLinks = useMemo(() => {
    const links = [
      {
        label: "Users",
        key: "/users"
      },
      {
        label: "Companies",
        key: "/companies"
      },
      {
        label: "Projects",
        key: "/projects"
      },
      {
        label: "Settings",
        key: "/settings"
      }
    ];

    if (
      isSubscriptionActivityLogEnabled &&
      subscriptionRole !== RoleSubscriptionEnum.subscription_viewer
    ) {
      links.splice(3, 0, {
        label: "Reports",
        key: "/reports"
      });
    }

    if (isUsageDashboardEnabled || isExecutiveOverviewDashboardEnabled) {
      links.splice(3, 0, {
        label: "Analytics",
        key: "/analytics"
      });
    }

    return links;
  }, [
    isUsageDashboardEnabled,
    isExecutiveOverviewDashboardEnabled,
    isSubscriptionActivityLogEnabled,
    subscriptionRole
  ]);

  const renderContent = () => {
    if (
      subscriptionRole === undefined ||
      subscriptionRole === RoleSubscriptionEnum.none
    ) {
      return (
        <div className="flex items-center justify-center h-full">
          <Spin size="large" />
        </div>
      );
    }

    if (!isValidRoute) {
      return <NotFoundPage />;
    }

    return (
      <div className="flex flex-col h-screen">
        <Navbar menuLinks={menuLinks} />
        <div className="grow overflow-auto min-h-0 flex flex-col">
          {children}
        </div>
      </div>
    );
  };

  return renderContent();
}

function ProjectLayout(props: LayoutProps) {
  const { children } = props;
  const { projectId } = useParams<{ projectId: string }>();

  const { pathname } = useLocation();

  const isPreconMenuActive = Boolean(
    matchPath(pathname, { path: `/project/${projectId}/design-packages` }) ||
    matchPath(pathname, { path: `/project/${projectId}/bid-packages` }) ||
    matchPath(pathname, { path: `/project/${projectId}/design-scope` })
  );
  usePreconModuleBodyClass(isPreconMenuActive);

  const menuLinks = useMemo(() => {
    const moduleLink = (
      label: string,
      icon: ReactNode,
      path: string
    ): { label: ReactNode; key: string } => ({
      label: (
        <div className="flex items-center space-x-2">
          {icon}
          <div>{label}</div>
        </div>
      ),
      key: path
    });

    const designLink = moduleLink(
      "Design Packages",
      <DesignPackageCiqIcon className="stroke-current" size={14} opacity="1" />,
      `/project/${projectId}/design-packages`
    );
    const designScopeLink = moduleLink(
      "Design Scope",
      <DesignScopeCiqIcon className="stroke-current" size={14} opacity="1" />,
      `/project/${projectId}/design-scope`
    );
    const bidPackageLink = moduleLink(
      "Bid Packages",
      <BidPackageCiqIcon className="fill-inherit" size={14} opacity="1" />,
      `/project/${projectId}/bid-packages`
    );
    const submittalsLink = moduleLink(
      "Submittals",
      <SubmittalCiqIcon className="fill-inherit" size={14} opacity="1" />,
      `/project/${projectId}/submittals`
    );
    const materialsLink = moduleLink(
      "Materials",
      <MaterialCiqIcon className="fill-inherit" size={14} opacity="1" />,
      `/project/${projectId}/materials`
    );
    const schedulesLink = moduleLink(
      "Schedules",
      <ScheduleCiqIcon className="fill-inherit" size={14} opacity="1" />,
      `/project/${projectId}/schedule`
    );

    return [
      {
        label: (
          <div className="flex items-center space-x-2">
            <PreconCiqIcon className="fill-inherit" size={14} opacity="1" />
            <div>Precon</div>
            <Tag
              className={
                isPreconMenuActive
                  ? "!border-current !bg-transparent !text-ciq-golden-purple-500"
                  : "!border-current !bg-transparent !text-white group-hover:!text-ciq-golden-purple-500"
              }
            >
              Beta
            </Tag>
          </div>
        ),
        key: "precon",
        children: [designLink, designScopeLink, bidPackageLink]
      },
      designLink,
      designScopeLink,
      bidPackageLink,
      {
        label: (
          <div className="flex items-center space-x-2">
            <ConstructionCiqIcon
              className="fill-inherit"
              size={14}
              opacity="1"
            />
            <div>Construction</div>
          </div>
        ),
        key: "construction",
        children: [submittalsLink, materialsLink, schedulesLink]
      },
      submittalsLink,
      materialsLink,
      schedulesLink,
      {
        label: "Reports",
        key: `/project/${projectId}/reports`
      },
      {
        label: "Settings",
        key: `/project/${projectId}/settings`
      }
    ];
  }, [isPreconMenuActive, projectId]);
  const projectContext = useContext(ProjectContext);
  const userRole = projectContext?.tokenContents?.role;
  const isValidRoute = useValidateRouteAccess({ userRole });
  const renderContent = () => {
    if (!userRole) {
      return (
        <div className="flex items-center justify-center h-full">
          <Spin size="large" />
        </div>
      );
    }

    if (!isValidRoute) {
      return <NotFoundPage />;
    }

    if (matchPath(pathname, { path: `/project/${projectId}/printView` })) {
      return <div>{children}</div>;
    }

    const isDashboardRoute = Boolean(
      matchPath(pathname, {
        path: `/project/${projectId}/dashboard`,
        exact: true
      })
    );
    const projectDetailsKnown =
      Boolean(projectContext.projectDetails) ||
      Boolean(projectContext.projectDetailsQuerySettled);
    const waitingForHomeDecision =
      isDashboardRoute &&
      !projectContext.projectDetailsFailed &&
      (!projectDetailsKnown ||
        !projectContext.isProductEntitlementsDataAvailable);
    if (waitingForHomeDecision) {
      return (
        <div className="flex items-center justify-center h-full">
          <Spin size="large" />
        </div>
      );
    }

    const openDesignPackages = isPreconstructionProjectHome(
      projectContext.projectDetails,
      projectContext.isFeatureFlagEnabled("BUYOUT"),
      !isPermissionNotGrantted(
        ProjectPermissionEnum.ViewDesignPage,
        userRole ?? ""
      )
    );
    if (openDesignPackages && isDashboardRoute) {
      return <Redirect to={getProjectHomePath(projectId, true)} />;
    }

    return (
      <div className="flex flex-col h-screen">
        {matchPath(pathname, { path: `/project/${projectId}/dashboard` }) ? (
          <ProjectDashboardNavBar />
        ) : (
          <Navbar menuLinks={menuLinks} />
        )}
        <ArchivedProjectBanner />
        <div className="grow overflow-auto">{children}</div>
      </div>
    );
  };

  return (
    <>
      <ErrorBoundary>
        <ExtractionCompletionAlert />
      </ErrorBoundary>
      <TourProvider>{renderContent()}</TourProvider>
    </>
  );
}

function ProjectScheduleLayout(props: LayoutProps) {
  const { children } = props;
  const { projectId } = useParams<{ projectId: string }>();
  const { pathname } = useLocation();
  const { isFeatureFlagEnabled }: TProjectContext = useContext(ProjectContext);

  const menuLinks = useMemo(
    () => [
      {
        label: "Project Schedule",
        key: `/project/${projectId}/schedule/project-schedules`
      },
      {
        label: "Change Impact",
        key: `/project/${projectId}/schedule/change-impact`
      },
      {
        label: "Submittal Linking",
        key: `/project/${projectId}/schedule/submittal-linking`
      },
      {
        label: "Material Linking",
        key: `/project/${projectId}/schedule/material-linking`
      }
    ],
    [projectId]
  );

  const isOpcIntegrationEnabled = isFeatureFlagEnabled("OPC_INTEGRATION_V1");
  const hasOpcImportFailure = useHasOpcImportFailure();

  // Second-level tabs belong to the Project Schedule tab only — the other
  // top-level tabs have no sub-pages. The OPC tab is deliberately a *child* of
  // the project-schedules path: CIQTabs matches non-exactly, so nesting keeps
  // "Project Schedule" underlined in the row above while a sub-tab is active.
  const subMenuLinks = useMemo(
    () => [
      {
        label: "Schedules",
        key: `/project/${projectId}/schedule/project-schedules`
      },
      {
        label: "Schedules in OPC",
        key: `/project/${projectId}/schedule/project-schedules/opc`,
        alertTooltip: hasOpcImportFailure ? "Import failed" : undefined
      }
    ],
    [projectId, hasOpcImportFailure]
  );

  // Without the OPC tab there is only one sub-tab left, so the whole row is
  // suppressed rather than rendering a lone tab.
  const showSubTabs =
    isOpcIntegrationEnabled &&
    subMenuLinks.some((link) =>
      matchPath(pathname, { path: link.key, exact: true })
    );

  return (
    <div className="flex flex-col h-full gap-y-2">
      <CIQTabs menuLinks={menuLinks} key={projectId} />
      {showSubTabs && <CIQSubTabs menuLinks={subMenuLinks} />}
      {children}
    </div>
  );
}

function PrivateRoutes() {
  // GA4 identity, plus the subscription/project user properties where tracking
  // runs (prod allowlist) so attribution obeys the same gate as the heartbeat.
  // Deliberately no dependency array: both reads come from the JWT rather than
  // props/state, so this has to re-sync after a subscription or project switch.
  useEffect(() => {
    setGAUserId();
    if (isTabTrackingEnabledForHost()) {
      updateGAUserProperties();
    }
  });
  const { gqlClient, exchangeFailed } = useGetGQLClientForSubscription();
  // Recover the session when the tab is reactivated after being backgrounded or
  // asleep (when the proactive refresh timer was throttled/paused).
  useEffect(() => installTokenRefreshOnWake(), []);
  // Idle-session policy: end the session after the inactivity window with no
  // interaction in any tab.
  useEffect(() => installInactivityLogout(), []);
  // Report concurrent multi-tab usage (prod only); cleanup stops the timer and
  // clears the Sentry tab_count tag on logout/unmount.
  useEffect(() => installTabTracking(), []);
  if (exchangeFailed) return <NotFoundPage />;
  if (!gqlClient) return <PrivateRoute />;
  return (
    <ApolloProvider client={gqlClient}>
      <SubscriptionProvider>
        <Switch>
          <PrivateRoute
            exact
            path="/analytics/export"
            component={SubscriptionReportsExport}
          />
          <PrivateRoute
            exact
            path="/reports/printView"
            component={PrintViewLayout}
          />
          <PrivateRoute
            exact
            path={["/home", "/new-project", "/change_password"]}
          >
            <NoMenuLayout>
              <PrivateRoute exact path="/home" component={HomePage} />
              <PrivateRoute
                exact
                path="/new-project"
                component={CreateProjectPage}
              />
              <PrivateRoute
                exact
                path="/change_password"
                component={ChangePassword}
              />
            </NoMenuLayout>
          </PrivateRoute>
          <PrivateRoute
            exact
            path={[
              "/users",
              "/companies",
              "/projects",
              "/analytics",
              "/reports",
              "/reports/audit-reports",
              "/reports/audit-reports/activity-log",
              "/settings",
              "/settings/:tab1Id",
              "/settings/:tab1Id/:tab2Id",
              "/settings/:tab1Id/:tab2Id/:tab3Id"
            ]}
          >
            <HomeLayout>
              <PrivateRoute exact path="/users" component={Users} />
              <PrivateRoute exact path="/companies" component={CompanyList} />
              <PrivateRoute
                exact
                path="/projects"
                component={ProjectsListpage}
              />
              <PrivateRoute
                exact
                path="/analytics"
                component={SubscriptionReports}
              />
              <PrivateRoute
                exact
                path={["/reports", "/reports/audit-reports"]}
                component={SubscriptionReportsMenu}
              />
              <PrivateRoute
                exact
                path="/reports/audit-reports/activity-log"
                component={ActivityLogReport}
              />
              <PrivateRoute
                exact
                path={[
                  "/settings",
                  "/settings/:tab1Id",
                  "/settings/:tab1Id/:tab2Id",
                  "/settings/:tab1Id/:tab2Id/:tab3Id"
                ]}
                component={SubscriptionSettingsPage}
              />
            </HomeLayout>
          </PrivateRoute>
          <PrivateRoute
            path="/project/:projectId/reports/submittals/export"
            exact
          >
            <ProjectProvider>
              <PrivateRoute
                exact
                path="/project/:projectId/reports/submittals/export"
                component={RiskReportExportPDF}
              />
            </ProjectProvider>
          </PrivateRoute>
          <PrivateRoute path="/project/:projectId/schedule/impact/export" exact>
            <ProjectProvider>
              <PrivateRoute
                exact
                path="/project/:projectId/schedule/impact/export"
                component={ScheduleImpactExportPDF}
              />
            </ProjectProvider>
          </PrivateRoute>
          <PrivateRoute path="/project/:projectId/analytics/export" exact>
            <ProjectProvider>
              <PrivateRoute
                exact
                path="/project/:projectId/analytics/export"
                component={GCProjectDashboardExport}
              />
            </ProjectProvider>
          </PrivateRoute>
          <PrivateRoute
            path="/project/:projectId/schedule/change-impact/printview"
            exact
          >
            <ProjectProvider>
              <PrivateRoute
                exact
                path="/project/:projectId/schedule/change-impact/printview"
                component={OptimizedPrintView}
              />
            </ProjectProvider>
          </PrivateRoute>
          <PrivateRoute path="/project/:projectId/custom-excel-export" exact>
            <ProjectProvider>
              <PrivateRoute
                exact
                path="/project/:projectId/custom-excel-export"
                component={CustomExcelExportView}
              />
            </ProjectProvider>
          </PrivateRoute>
          <PrivateRoute path={["/project/:projectId"]}>
            <ProjectProvider>
              <FeatureLogNavProvider>
                <ProjectLayout>
                  <Switch>
                    <PrivateRoute
                      exact
                      path="/project/:projectId/dashboard"
                      component={ProjectDashboardPage}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/design"
                      component={DesignPage}
                    />
                    {/* <PrivateRoute
                    exact
                    path="/project/:projectId/:logPageType"
                    component={EntityLogPage}
                  /> */}
                    <PrivateRoute
                      exact
                      path="/project/:projectId/submittals"
                      component={SubmittalLogLandingPage}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/submittals-voided"
                      component={SubmittalLogLandingPage}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/submittals-deleted"
                      component={SubmittalLogLandingPage}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/submittals-untracked"
                      component={SubmittalLogUntracked}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/submittals/:submittalId"
                      component={SubmittalDetailspage}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/bid-packages"
                      component={PreconstructionLogPage}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/bid-packages/:bidPackageId"
                      component={PreconstructionDetailsPage}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/design-packages"
                      component={DesignPackageLogPage}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/design-packages/:designPackageId/details"
                      component={DesignPackageDetailPage}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/design-packages/:designPackageId"
                      component={DesignPackageLogPage}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/design-scope"
                      component={DesignScopePage}
                    />
                    <PrivateRoute
                      exact
                      path={[
                        "/project/:projectId/settings",
                        "/project/:projectId/settings/:tab1Id",
                        "/project/:projectId/settings/:tab1Id/:tab2Id",
                        "/project/:projectId/settings/:tab1Id/:tab2Id/:tab3Id"
                      ]}
                      component={ProjectSettingManu}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/materials"
                      component={MaterialFeatureLogPage}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/materials/extracted"
                      component={MaterialExtractedLogPage}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/materials/sc-request"
                      component={InternalScRequestPage}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/materials/:materialId"
                      component={FeatureMaterialDetailsLandingPage}
                    />

                    <PrivateRoute
                      exact
                      path="/project/:projectId/schedule"
                      component={ScheduleMenuTabs}
                    />

                    <Redirect
                      exact
                      from="/project/:projectId/reports"
                      to="/project/:projectId/reports/project-reports"
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/reports/project-reports"
                      component={ReportsPageManu}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/reports/audit-reports"
                      component={ReportsPageManu}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/reports/project-reports/risk"
                      component={RiskReportPage}
                    />
                    <PrivateRoute
                      path="/project/:projectId/reports/project-reports/upcoming"
                      component={UpcomingSubmittals}
                    />
                    <PrivateRoute
                      path="/project/:projectId/reports/project-reports/activities"
                      component={ScheduleActivitiesReport}
                    />
                    <PrivateRoute
                      exact
                      path="/project/:projectId/reports/audit-reports/activity-log"
                      component={ActivityLogReport}
                    />
                    <PrivateRoute
                      path="/project/:projectId/reports/project-reports/procurement-follow-ups"
                      component={InAppUpcomingMilestonesReport}
                    />
                    <PrivateRoute
                      path="/project/:projectId/reports/project-reports/custom-report/:reportId"
                      component={CustomReportPage}
                    />
                    <PrivateRoute
                      path="/project/:projectId/printView"
                      component={PrintViewLayout}
                    />

                    <ProjectScheduleLayout>
                      <PrivateRoute
                        exact
                        path="/project/:projectId/schedule/project-schedules"
                        component={ProjectSchedules}
                      />
                      <PrivateRoute
                        exact
                        path="/project/:projectId/schedule/project-schedules/opc"
                        component={SchedulesInOpc}
                      />
                      <PrivateRoute
                        exact
                        path="/project/:projectId/schedule/project-schedule/:selectedVersionId"
                        component={FeatureScheduleDetailsLandingPage}
                      />
                      <PrivateRoute
                        exact
                        path="/project/:projectId/schedule/change-impact"
                        component={ProjectScheduleCompare}
                      />

                      <PrivateRoute
                        exact
                        path="/project/:projectId/schedule/submittal-linking"
                        component={FeatureSubmittalLinking}
                      />
                      <PrivateRoute
                        exact
                        path="/project/:projectId/schedule/material-linking"
                        component={FeatureMaterialLinking}
                      />
                    </ProjectScheduleLayout>
                    <PrivateRoute>
                      <Route component={NotFoundPage} />
                    </PrivateRoute>
                  </Switch>
                </ProjectLayout>
              </FeatureLogNavProvider>
            </ProjectProvider>
          </PrivateRoute>
          <PrivateRoute>
            <Route component={NotFoundPage} />
          </PrivateRoute>
        </Switch>
      </SubscriptionProvider>
    </ApolloProvider>
  );
}

function App() {
  const [isTokenLoaded, setIsTokenLoaded] = useState(false);

  // App-wide (login included): sticky offline notice + back-online
  // confirmation. This is the ONLY offline messaging — per-request error
  // toasts are suppressed while offline so the user hears one voice.
  useEffect(() => installConnectivityNotices(), []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const pathname = window.location.pathname;

    if (!code) {
      setIsTokenLoaded(true);
      return;
    }

    // OAuth SSO: exchange code in SSOCallback (not tmp_token).
    if (pathname === "/callback") {
      setIsTokenLoaded(true);
      return;
    }

    // IdP often returns to /?code= — hand off to the SSO callback route.
    if (pathname === "/" || pathname === "") {
      window.location.replace(`/callback${window.location.search}`);
      return;
    }

    setAccessTokenAndSubscriptionId().finally(() => {
      setIsTokenLoaded(true);
    });
  }, []);

  useCheckVersionUpdates({ autoCheckInIntervals: true });

  const isForgotPassword = !!new URLSearchParams(
    globalThis.location.search
  ).get("forgot-password");
  const isSubmitLeadTimeRequest =
    globalThis.location.pathname === "/submit-lead-time";

  if (!isTokenLoaded) {
    return null;
  }

  return (
    <Router>
      <PageTitleProvider>
        <TokenValidationGate skip={isForgotPassword || isSubmitLeadTimeRequest}>
          <Switch>
            <Route exact path="/" component={SSOLogin} />
            <Route exact path="/Login" component={Login} />
            <Route exact path="/callback" component={SSOCallback} />
            <Route
              exact
              path="/subscription-select"
              component={SubscriptionSelect}
            />
            <Route exact path="/set_password" component={SetupAccount} />
            <Route exact path="/reset_password" component={SetPassword} />
            <Route exact path="/account-locked" component={AccountLocked} />
            <Route exact path="/forgot_password" component={ForgetPassword} />
            <Route
              exact
              path="/reset_expired_password"
              component={ChangePasswordOnExpiry}
            />
            <Route exact path="/accept-invite" component={AcceptInvitePage} />
            <Route
              exact
              path="/submit-lead-time"
              component={MaterialPlanning}
            />

            <Route exact path="/admin">
              <AdminApp />
            </Route>
            <Route component={PrivateRoutes} />
          </Switch>
        </TokenValidationGate>
      </PageTitleProvider>
    </Router>
  );
}

// TODO: Need to move to a separate definition file
declare global {
  interface Window {
    JSREPORT_READY_TO_START: any; // this will be your variable name
  }
}

export default App;
